<template>
  <section aria-labelledby="events-title" class="d-flex flex-column gap-3">
    <div class="d-flex justify-content-between align-items-center">
      <h2 id="events-title" class="h4 mb-0 d-flex align-items-center gap-2">
        <span class="dashboard-area-icon" aria-hidden="true"><font-awesome-icon icon="skiing" /></span>Ausfahrten
      </h2>
      <router-link to="/event-view">Alle Ausfahrten</router-link>
    </div>

    <div v-if="selectedEvent" class="d-flex align-items-center gap-2" role="group" aria-label="Ausfahrt wechseln">
      <button type="button" class="btn btn-outline-secondary btn-square flex-shrink-0" aria-label="Vorherige Ausfahrt anzeigen" title="Vorherige Ausfahrt"
        :disabled="!previousEvent" @click="selectEvent(previousEvent)">
        <font-awesome-icon icon="chevron-left" />
      </button>
      <router-link class="next-trip flex-column align-items-stretch text-decoration-none flex-grow-1 min-w-0"
        :to="{ name: 'eventoverview', params: { id: selectedEvent.id } }">
        <span class="d-flex align-items-center gap-3">
          <span class="d-flex flex-column gap-1 flex-grow-1 min-w-0">
            <span class="text-label next-trip-muted">{{ selectedEventLabel }}</span>
            <span class="fs-4 fw-bold text-truncate">{{ selectedEvent.name }}</span>
            <span class="next-trip-muted">{{ selectedEventDateLine }}</span>
          </span>
          <span v-if="booking" class="booking-ring">
            <svg viewBox="0 0 64 64" role="img" :aria-label="`${booking.currentBookings} von ${booking.maxBooking} Plätzen gebucht`">
              <circle cx="32" cy="32" :r="RING_RADIUS" fill="none" stroke="#a84646" stroke-width="6" />
              <circle cx="32" cy="32" :r="RING_RADIUS" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"
                :stroke-dasharray="ringDash" />
            </svg>
            <span class="booking-ring-label">
              <span class="fs-5 fw-semibold">{{ booking.currentBookings }}</span>
              <span class="small next-trip-muted">von {{ booking.maxBooking }}</span>
            </span>
          </span>
        </span>
        <span v-if="booking" class="next-trip-booking">
          <span class="d-flex justify-content-between align-items-baseline gap-2">
            <span class="text-label next-trip-muted">Buchungsstand</span>
            <span class="small">{{ bookingSummary }}</span>
          </span>
          <span class="next-trip-bar" role="progressbar" aria-label="Buchungsstand" :aria-valuenow="bookingPercent"
            aria-valuemin="0" aria-valuemax="100">
            <span class="next-trip-bar-fill" :style="{ width: bookingPercent + '%' }"></span>
          </span>
        </span>
      </router-link>
      <button type="button" class="btn btn-outline-secondary btn-square flex-shrink-0" aria-label="Folgende Ausfahrt anzeigen" title="Folgende Ausfahrt"
        :disabled="!followingEvent" @click="selectEvent(followingEvent)">
        <font-awesome-icon icon="chevron-right" />
      </button>
    </div>

    <div class="row row-cols-3 g-2 g-lg-3">
      <div class="col">
        <StatCard label="Freigabe offen" :value="openRequests.length" :attention="openRequests.length > 0" />
      </div>
      <div class="col">
        <StatCard label="Freie Plätze" :value="booking ? booking.freeSeats : '–'" />
      </div>
      <div class="col">
        <StatCard label="Busplätze" :value="booking ? booking.currentBusBookings : '–'" />
      </div>
    </div>

    <section aria-labelledby="events-todo-title" class="d-flex flex-column gap-2">
      <div class="d-flex justify-content-between align-items-baseline">
        <h3 id="events-todo-title" class="h5 mb-0">Zu erledigen</h3>
        <router-link v-if="selectedEvent && openRequests.length > 0"
          :to="{ name: 'participantlist', params: { id: selectedEvent.id }, query: { filter: 'open' } }">Alle ansehen</router-link>
      </div>
      <p v-if="!selectedEvent" class="text-body-secondary mb-0">Sobald eine Ausfahrt angelegt ist, erscheinen hier offene Aufgaben.</p>
      <p v-else-if="openRequests.length === 0" class="text-body-secondary mb-0">Alles erledigt – keine offenen Anmeldungen.</p>
      <ul v-else class="list-unstyled d-flex flex-column gap-2 mb-0">
        <li v-for="participant in visibleRequests" :key="participant.id" class="todo-row">
          <span class="todo-dot" aria-hidden="true"></span>
          <span class="d-flex flex-column flex-grow-1 min-w-0">
            <span class="fw-medium text-truncate">{{ participant.firstname }} {{ participant.lastname }} freischalten</span>
            <span class="small text-body-secondary text-truncate">{{ participantMeta(participant) }}</span>
          </span>
          <router-link class="btn btn-primary btn-square"
            :to="{ name: 'participantlist', params: { id: selectedEvent.id }, query: { filter: 'open', participant: participant.id } }"
            :aria-label="`${participant.firstname} ${participant.lastname} öffnen`">
            <font-awesome-icon icon="chevron-right" />
          </router-link>
        </li>
      </ul>
    </section>
  </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import StatCard from "@/components/common/StatCard.vue";
import { EVENT_NAMESPACE, PARTICIPANT_NAMESPACE } from "@/store/namespace-types";
import { GET_EVENT_DETAILS, GET_PARTICIPANTS } from "@/store/action-types";
import { daysUntil, formatDate, nextEvent, relativeDays, sortByStartDate } from "@/composables/dateFormat";
import { isDeleted, isGuest, isMember, needsRelease } from "@/composables/participantStatus";
import { GET_PARTICIPANTS_FOR_EVENT } from "@/store/getters-types";

defineOptions({ name: "EventsDashboard" });

const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const MAX_TODOS = 5;

const store = useStore();

const events = computed(() => Object.values(store.state[EVENT_NAMESPACE].events ?? {}));
const sortedEvents = computed(() => sortByStartDate(events.value));
const upcoming = computed(() => nextEvent(events.value));

// Defaults to the next Ausfahrt until the user steps to another one.
const selectedEventId = ref(null);
const selectedEvent = computed(
  () => sortedEvents.value.find((event) => String(event.id) === String(selectedEventId.value)) ?? upcoming.value
);
const selectedIndex = computed(() => sortedEvents.value.indexOf(selectedEvent.value));
const previousEvent = computed(() => (selectedIndex.value > 0 ? sortedEvents.value[selectedIndex.value - 1] : null));
const followingEvent = computed(() =>
  selectedIndex.value >= 0 ? sortedEvents.value[selectedIndex.value + 1] ?? null : null
);
const selectEvent = (event) => {
  selectedEventId.value = event.id;
};

const selectedEventLabel = computed(() => {
  const days = daysUntil(selectedEvent.value?.startDate);
  if (days !== null && days < 0) return "Vergangene Ausfahrt";
  return selectedEvent.value === upcoming.value ? "Nächste Ausfahrt" : "Kommende Ausfahrt";
});
const selectedEventDateLine = computed(() => {
  if (!selectedEvent.value) return "";
  const date = formatDate(selectedEvent.value.startDate);
  return date ? `${date} · ${relativeDays(daysUntil(selectedEvent.value.startDate))}` : "";
});

const details = computed(() => {
  const current = store.state[EVENT_NAMESPACE].currentEvent;
  return current && selectedEvent.value && String(current.event?.id) === String(selectedEvent.value.id) ? current : null;
});
const booking = computed(() => details.value?.eventBookingInfo ?? null);
const bookingShare = computed(() => {
  const max = booking.value?.maxBooking ?? 0;
  return max > 0 ? Math.min(1, booking.value.currentBookings / max) : 0;
});
const bookingPercent = computed(() => Math.round(bookingShare.value * 100));
const bookingSummary = computed(() => {
  if (!booking.value) return "";
  return `${booking.value.currentBookings} gebucht · ${booking.value.freeSeats} frei · ${bookingPercent.value} %`;
});
const ringDash = computed(
  () => `${(bookingShare.value * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`
);

const openRequests = computed(() => {
  const list = store.getters[`${PARTICIPANT_NAMESPACE}/${GET_PARTICIPANTS_FOR_EVENT}`](selectedEvent.value?.id);
  return (list ?? []).filter((p) => !isDeleted(p) && needsRelease(p));
});
const visibleRequests = computed(() => openRequests.value.slice(0, MAX_TODOS));

const participantMeta = (participant) => {
  const courses = details.value?.courses ?? [];
  const course = courses.find((c) => c.value == participant.course)?.text;
  const membership = isMember(participant) ? "Mitglied" : isGuest(participant) ? "Gast" : null;
  return [course, membership].filter(Boolean).join(" · ");
};

const loadEvent = (id) => {
  if (id == null) return;
  store.dispatch({ type: `${EVENT_NAMESPACE}/${GET_EVENT_DETAILS}`, eventId: id });
  store.dispatch({ type: `${PARTICIPANT_NAMESPACE}/${GET_PARTICIPANTS}`, eventId: id });
};

watch(() => selectedEvent.value?.id, loadEvent, { immediate: true });
</script>

<style scoped>
.booking-ring svg circle {
  transition: stroke-dasharray 0.3s ease;
}
</style>
